import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from "./ui/alert"; 
import { LoadingSpinner } from './ui/loading-spinner'; 
import { subscriptionService } from '../services/subscriptionService'; 
import { useToast } from '../hooks/use-toast'; 
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase'; 
import HelpIcon from './ui/HelpIcon'; 

// Import the specialized display components
import SubscriptionPlansDisplay from './billing/SubscriptionPlansDisplay';
import TrialEndedNotice from './billing/TrialEndedNotice';
import ActiveSubscriptionDisplay from './billing/ActiveSubscriptionDisplay';
import ExpiredSubscriptionDisplay from './billing/ExpiredSubscriptionDisplay'; 

// Define ROUTES
const ROUTES = {
  DASHBOARD: '/dashboard',
};

const BillingSettings = ({ 
  user, 
  onSubscriptionUpdate
}) => {
  const { 
    currentUser: currentUserFromContext, 
    subscriptionStatus: subscriptionStatusFromContext,
    refreshSubscriptionContext
  } = useOutletContext() || {};
  
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const propIsOnboarding = location.state?.onboarding || false;
  const propForceShowPlans = location.state?.forceShowPlans || false;

  // State management
  const [subscription, setSubscription] = useState(
    subscriptionStatusFromContext?.subscription || { status: 'loading', level: 'free' }
  );
  const [subscriptionSynced, setSubscriptionSynced] = useState(
    subscriptionStatusFromContext?.synced || false
  );
  const [loading, setLoading] = useState(!subscriptionStatusFromContext);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    subscriptionStatusFromContext?.lastCheckError || ''
  );
  const [isTrialStarted, setIsTrialStarted] = useState(false);
  const [completingOnboardingFlag, setCompletingOnboardingFlag] = useState(propIsOnboarding);
  const [hasChosenPlan, setHasChosenPlan] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Add missing useRef declarations
  const portalReturnHandled = useRef(false);
  const initialRefreshComplete = useRef(false);
  const isRefreshingRef = useRef(false); // Add ref to prevent multiple simultaneous refreshes

  // Handler functions
  const handleRefreshSubscription = useCallback(async () => {
    if (!user?.uid || refreshing || isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    setRefreshing(true);
    setErrorMessage('');
    
    try {
      if (refreshSubscriptionContext) {
        await refreshSubscriptionContext();
      } else {
        const updatedStatus = await subscriptionService.getUserSubscription(user.uid, { forceRefresh: true });
        if (onSubscriptionUpdate) onSubscriptionUpdate(updatedStatus);
      }
      
      setErrorMessage(''); // Clear errors on successful refresh
    } catch (error) {
      const errorMsg = error.message || "Failed to refresh subscription.";
      setErrorMessage(errorMsg);
    } finally {
      setRefreshing(false);
      isRefreshingRef.current = false;
    }
  }, [user, refreshing, refreshSubscriptionContext, onSubscriptionUpdate]);

  // Handle Stripe return status
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    
    if (status === 'success' && sessionId) {
      // Payment successful - show inline success message instead of toast
      setErrorMessage('');
      
      // Refresh subscription data
      handleRefreshSubscription();
      
      // Clean up URL
      searchParams.delete('status');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      
    } else if (status === 'cancel') {
      // Payment cancelled - show inline message instead of toast
      setErrorMessage('Your payment was cancelled. You can try again when ready.');
      
      // Clean up URL
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]); // Removed handleRefreshSubscription from dependencies to avoid circular dependency

  // Robust trial eligibility check
  const availedTrials = useMemo(() => {
    const trials = (subscriptionStatusFromContext?.subscription?.availedTrials && 
                    typeof subscriptionStatusFromContext.subscription.availedTrials === 'object') 
                   ? subscriptionStatusFromContext.subscription.availedTrials 
                   : {};
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] Raw availedTrials from context:', trials);
      console.log('[BillingSettings] subscriptionStatusFromContext:', subscriptionStatusFromContext);
      console.log('[BillingSettings] Data source:', subscriptionStatusFromContext?.source);
      console.log('[BillingSettings] Data synced:', subscriptionStatusFromContext?.synced);
      console.log('[BillingSettings] Data from cache:', subscriptionStatusFromContext?.fromCache);
    }
    
    // NOTE: Debug confirm removed — was causing alert popup every render in dev mode
    // The logic below handles trial eligibility properly without manual intervention
    
    // CRITICAL FIX: More conservative approach - only return used trials if we have clear evidence
    // Check if user has explicit trial usage records
    const hasExplicitTrialUsage = Object.keys(trials).length > 0 && 
                                  Object.values(trials).some(used => used === true);
    
    const hasStripeInteraction = !!(
      subscriptionStatusFromContext?.subscription?.stripeCustomerId || 
      subscriptionStatusFromContext?.subscription?.stripeSubscriptionId ||
      currentUserFromContext?.stripeCustomerId
    );
    
    // Only return used trials if we have both explicit usage AND Stripe interaction
    if (hasExplicitTrialUsage && hasStripeInteraction) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[BillingSettings] User has used trials with Stripe interaction, returning:', trials);
      }
      return trials;
    }
    
    // For all other cases, allow trials (be permissive for edge cases)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] Allowing trials - no clear evidence of prior usage');
    }
    return {};
  }, [subscriptionStatusFromContext, currentUserFromContext]);

  // Enhanced display mode logic to handle cancel_at_period_end
  const displayMode = useMemo(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] DisplayMode calculation:', {
        subscription,
        subscriptionSynced,
        availedTrials,
        isTrialStarted,
        propForceShowPlans,
        completingOnboardingFlag
      });
    }

    if (!isTrialStarted && !subscriptionSynced && !propForceShowPlans && !completingOnboardingFlag) {
      return 'loading';
    }

    if (subscription?.status === 'error') {
      return 'error';
    }
    
    // Handle past_due status as expired subscription
    if (subscription?.status === 'past_due' || 
        subscription?.status === 'unpaid' ||
        subscription?.status === 'incomplete_expired') {
      return 'expired';
    }
    
    // More robust active subscription check including cancel_at_period_end
    const isActiveSub = (subscription?.status === 'active' || subscription?.status === 'trialing') && 
                        (subscription?.level === 'basic' || subscription?.level === 'pro') &&
                        subscription?.level !== 'free';
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] Active subscription check:', {
        subscriptionStatus: subscription?.status,
        subscriptionLevel: subscription?.level,
        isActiveSub,
        isTrialStarted
      });
    }
    
    if (isTrialStarted || isActiveSub) {
      return 'active';
    }
    
    // CRITICAL FIX: Check trial ended status BEFORE checking force flags
    // Better trial ended detection with comprehensive logging
    const hasAvailedTrialBefore = Object.keys(availedTrials).length > 0 && 
                                  Object.values(availedTrials).some(used => used === true);
    
    // Check all possible sources for Stripe customer ID
    const hasStripeCustomerId = !!(
      subscription?.stripeCustomerId || 
      subscriptionStatusFromContext?.subscription?.stripeCustomerId ||
      currentUserFromContext?.stripeCustomerId || 
      user?.stripeCustomerId
    );
    
    // Check if subscription indicates free/inactive status
    const isInactiveSubscription = (
      subscription?.status === 'free' || 
      subscription?.status === 'inactive' || 
      !subscription?.status ||
      subscription?.level === 'free'
    );
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] Trial ended detection:', {
        availedTrials,
        hasAvailedTrialBefore,
        hasStripeCustomerId,
        isInactiveSubscription,
        subscriptionStatus: subscription?.status,
        subscriptionLevel: subscription?.level,
        stripeCustomerSources: {
          fromSubscription: subscription?.stripeCustomerId,
          fromContext: subscriptionStatusFromContext?.subscription?.stripeCustomerId,
          fromCurrentUser: currentUserFromContext?.stripeCustomerId,
          fromUser: user?.stripeCustomerId
        }
      });
    }
    
    // Only block trials if they have explicit trial usage and subscription is inactive
    if (hasAvailedTrialBefore && isInactiveSubscription) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[BillingSettings] User has used trials and has inactive subscription, showing trialEnded');
      }
      return 'trialEnded';
    }
    
    // MOVED: Now check force flags after trial-ended check
    if (propForceShowPlans) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[BillingSettings] Forced to show plans (after trial check)');
      }
      return 'planSelection';
    }
    if (completingOnboardingFlag) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[BillingSettings] Onboarding mode (after trial check)');
      }
      return 'planSelection';
    }
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[BillingSettings] Defaulting to planSelection');
    }
    return 'planSelection';
  }, [
    subscription, subscriptionSynced, propForceShowPlans, completingOnboardingFlag,
    isTrialStarted, availedTrials, currentUserFromContext, user, subscriptionStatusFromContext
  ]);
  // Add a new handler for upgrading from Basic to Pro trial
  const handleUpgradeToPro = useCallback(async () => {
    if (!user || !user.uid || !user.email) {
      setErrorMessage("User not identified or email missing. Please try again.");
      return;
    }
    
    // Check if user is currently on a Basic trial
    if (subscription?.level !== 'basic' || subscription?.status !== 'trialing') {
      setErrorMessage("Upgrade to Pro trial is only available for users on Basic trial.");
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      // Call startTrial with 'pro' plan and user's email
      // Note: This will upgrade to Pro but maintain the same trial end date
      const newProTrialSubscription = await subscriptionService.startTrial('pro', user.email); 
      
      if (newProTrialSubscription) {
        setErrorMessage(''); // Clear any previous errors
        
        // Update local state immediately for UI refresh
        setSubscription(newProTrialSubscription); 
        setIsTrialStarted(true);

        // Update context for global state consistency
        if (onSubscriptionUpdate) {
          onSubscriptionUpdate({ 
            synced: true, 
            fromCache: false, 
            subscription: newProTrialSubscription 
          });
        }
        
        if (refreshSubscriptionContext) {
          await refreshSubscriptionContext(); 
        }        // Trigger welcome email for Pro trial upgrade
        try {
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[BillingSettings] Attempting to trigger welcome email for Pro trial upgrade.`);
          }          const nameForEmail = user.displayName || currentUserFromContext?.name || user.email.split('@')[0];
          await subscriptionService.triggerTrialWelcomeEmail('pro', nameForEmail);
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[BillingSettings] Welcome email trigger called for Pro trial upgrade with name: ${nameForEmail}`);
          }
        } catch (emailError) {
          console.warn('[BillingSettings] Failed to trigger welcome email for pro upgrade:', emailError);
        }
        
      } else {
        throw new Error("Could not upgrade to Pro trial. No subscription data returned.");
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to upgrade to Pro trial.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [user, subscription, onSubscriptionUpdate, refreshSubscriptionContext, currentUserFromContext]);

  // Update the handleManageSubscription function to handle different subscription states
  const handleManageSubscription = async () => {
    const effectiveStripeCustomerId = subscription?.stripeCustomerId || 
                                     currentUserFromContext?.stripeCustomerId || 
                                     user?.stripeCustomerId;
    
    if (!user || !user.uid || !effectiveStripeCustomerId) {
      setErrorMessage("Cannot manage subscription. Customer ID missing or user not identified.");
      return;
    }
    setLoading(true);
    setErrorMessage('');
    portalReturnHandled.current = false;
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('fromPortal', 'true');
      
      const session = await subscriptionService.createPortalSession(user.uid, effectiveStripeCustomerId, returnUrl.toString());
      if (session && session.url) {
        window.location.href = session.url;
      } else {
        const errorMsg = session?.error || "Could not open subscription portal.";
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to open subscription portal.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const onSelectPlan = useCallback(async (planIdOrPriceId, isTrialFlow) => {
    if (!user || !user.uid || !user.email) {
      setErrorMessage("User not identified or email missing. Please try again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setHasChosenPlan(true);    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log("[BillingSettings] onSelectPlan called with:", { planIdOrPriceId, isTrialFlow });
      }
      
      if (isTrialFlow) {
        const trialResult = await subscriptionService.startTrial(planIdOrPriceId, user.email);
        
        if (trialResult) {
          setIsTrialStarted(true);
          setErrorMessage(''); // Clear any previous errors
          
          if (onSubscriptionUpdate) {
            onSubscriptionUpdate({ 
              synced: true, 
              fromCache: false, 
              subscription: trialResult
            });
          }
          if (refreshSubscriptionContext) refreshSubscriptionContext();

          // Mark onboarding as complete if applicable
          if (completingOnboardingFlag || hasChosenPlan) {
            await completeOnboarding(planIdOrPriceId);
          }          // Trigger welcome email for the selected trial plan
          try {
            if (process.env.NODE_ENV !== 'production') {
              console.log(`[BillingSettings] Attempting to trigger welcome email for plan: ${planIdOrPriceId}`);
            }            const nameForEmail = user.displayName || currentUserFromContext?.name || user.email.split('@')[0];
            await subscriptionService.triggerTrialWelcomeEmail(planIdOrPriceId, nameForEmail);
            if (process.env.NODE_ENV !== 'production') {
              console.log(`[BillingSettings] Welcome email trigger called for plan: ${planIdOrPriceId} with name: ${nameForEmail}`);
            }
          } catch (emailError) {
            console.warn('[BillingSettings] Failed to trigger welcome email:', emailError);
          }

        } else {
          const errorMsg = trialResult?.error || `Failed to start trial.`;
          setErrorMessage(errorMsg);
        }
      } else {
        // Regular subscription flow
        let actualPriceId = planIdOrPriceId;
        
        if (planIdOrPriceId === 'basic') {
          actualPriceId = process.env.REACT_APP_STRIPE_BASIC_PRICE_ID;
        } else if (planIdOrPriceId === 'pro') {
          actualPriceId = process.env.REACT_APP_STRIPE_PRO_PRICE_ID;
        }
        
        if (!actualPriceId || typeof actualPriceId !== 'string') {
          const errorMsg = `Invalid Price ID: ${planIdOrPriceId} -> ${actualPriceId}`;
          console.error("[BillingSettings] Error:", errorMsg);
          setErrorMessage(errorMsg);
          setLoading(false);
          return;
        }
        
        // Make sure we have a Stripe customer ID
        let stripeCustomerId = subscription?.stripeCustomerId;
          if (!stripeCustomerId) {
          if (process.env.NODE_ENV !== 'production') {
            console.log("[BillingSettings] Getting Stripe customer ID for:", user.uid);
          }
          const customerResult = await subscriptionService.ensureStripeCustomer(user.uid, user.email);          if (customerResult?.success && customerResult?.stripeCustomerId) {
            stripeCustomerId = customerResult.stripeCustomerId;
            if (process.env.NODE_ENV !== 'production') {
              console.log("[BillingSettings] Obtained customer ID:", stripeCustomerId);
            }
          } else {
            console.error("[BillingSettings] Failed to get customer ID:", customerResult);
            setErrorMessage('Could not retrieve your customer record. Please try again.');
            setLoading(false);
            return;
          }
        }
          const isActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
        const currentStripeSubscriptionId = isActiveSubscription ? (subscription?.stripeSubscriptionId || null) : null;
        
        if (process.env.NODE_ENV !== 'production') {
          console.log("[BillingSettings] Creating checkout with:", { 
            planIdOrPriceId, 
            actualPriceId,
            stripeCustomerId,
            currentStripeSubscriptionId,
            subscriptionStatus: subscription?.status,
            isActiveSubscription
          });
        }
        
        // Create checkout session
        const session = await subscriptionService.createCheckoutSession(
          user.uid, actualPriceId, false, currentStripeSubscriptionId, stripeCustomerId
        );
        
        if (session && session.url) {
          window.location.href = session.url; 
        } else {
          const errorMsg = session?.error || `Could not initiate subscription.`;
          setErrorMessage(errorMsg);
        }
      }
    } catch (error) {
      const errorMsg = error.message || `An unexpected error occurred.`;
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [user, subscription, completingOnboardingFlag, hasChosenPlan, onSubscriptionUpdate, refreshSubscriptionContext, currentUserFromContext]);

  const completeOnboarding = async (selectedPlanId) => {
    if (!user?.uid) return;
    const planToSave = typeof selectedPlanId === 'string' ? selectedPlanId : '';
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        billingComplete: true,
        onboardingComplete: true,
        lastStep: 'billing_complete',
        selectedPlan: planToSave,
        billingInProgress: false,
      });
      setCompletingOnboardingFlag(false);
      navigate(ROUTES.DASHBOARD); 
    } catch (error) {
      console.error('[BillingSettings] Error completing onboarding:', error);
      setErrorMessage("Could not save onboarding status.");
    }
  };

  // Add effect to sync loading state with context updates
  useEffect(() => {    // When subscription context is updated and synced, stop local loading
    if (subscriptionStatusFromContext?.synced === true && loading) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('BillingSettings: Subscription synced, stopping loading state');
      }
      setLoading(false);
    }
      // Also sync subscription data when context updates
    if (subscriptionStatusFromContext?.subscription) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('BillingSettings: Syncing subscription data from context');
      }
      setSubscription(subscriptionStatusFromContext.subscription);
      setSubscriptionSynced(subscriptionStatusFromContext.synced || false);
    }
  }, [subscriptionStatusFromContext, loading]);

  // Effect for initial load
  useEffect(() => {
    if (user?.uid && !subscriptionSynced && !initialRefreshComplete.current && !isRefreshingRef.current) {
      initialRefreshComplete.current = true;
      handleRefreshSubscription();
    }
  }, [user, handleRefreshSubscription, subscriptionSynced]);

  // Safety fallback: if still in loading state after 4 seconds, unlock plan selection
  useEffect(() => {
    if (subscriptionSynced) return;
    const timer = setTimeout(() => {
      setSubscriptionSynced(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, [subscriptionSynced]);

  // Add reactivate subscription handler for cancel_at_period_end
  const handleReactivateSubscription = useCallback(async () => {
    const effectiveStripeCustomerId = subscription?.stripeCustomerId || 
                                     currentUserFromContext?.stripeCustomerId || 
                                     user?.stripeCustomerId;
    
    if (!user || !user.uid || !effectiveStripeCustomerId) {
      setErrorMessage("Cannot reactivate subscription. Customer ID missing or user not identified.");
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('fromPortal', 'true');
      
      const session = await subscriptionService.createPortalSession(user.uid, effectiveStripeCustomerId, returnUrl.toString());
      if (session && session.url) {
        window.location.href = session.url;
      } else {
        const errorMsg = session?.error || "Could not open subscription portal for reactivation.";
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to open subscription portal for reactivation.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [user, subscription, currentUserFromContext]);
  // Main render method
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Billing Settings</h2>
        <HelpIcon sectionKey="BillingSettings" />
      </div>

      {errorMessage && (
        <Alert variant="destructive" className="mb-4 border-2 border-red-400 bg-red-50/80 dark:bg-red-950/50 backdrop-blur-sm shadow-lg">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <svg className="h-5 w-5 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-4 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                Trial Restriction
              </h4>
              <AlertDescription className="text-red-700 dark:text-red-300 text-sm leading-relaxed">
                {errorMessage}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {completingOnboardingFlag && displayMode === 'planSelection' && (
        <div className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold text-purple-800 dark:text-purple-200 mb-2">Complete Your Setup</h3> 
          <p className="text-purple-700 dark:text-purple-300 mb-4"> 
            You're almost done! Choose a trial plan below to complete your account setup and start using the application.
          </p>
          <p className="text-sm text-purple-600 dark:text-purple-400"> 
            All plans include a 60-day free trial with no credit card required.
          </p>
        </div>
      )}

      {/* Display different components based on displayMode */}
      {displayMode === 'loading' && (
        <div className="flex flex-col justify-center items-center py-10 gap-4 text-center">
          <LoadingSpinner size="large" />
          <p className="text-base font-medium text-accent-foreground">
            Setting up your account&hellip;
          </p>
          <p className="text-sm text-muted-foreground max-w-xs">
            We&rsquo;re preparing your billing profile. This can take up to a minute &mdash; please don&rsquo;t close this page.
          </p>
        </div>
      )}

      {displayMode === 'error' && (
        <Alert variant="destructive" className="mb-4 border-2 border-red-400 bg-red-50/80 dark:bg-red-950/50 backdrop-blur-sm shadow-lg">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <svg className="h-5 w-5 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-4 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                Subscription Error
              </h4>
              <AlertDescription className="text-red-700 dark:text-red-300 text-sm leading-relaxed">
                Error loading subscription: {subscription.message || 'Please try refreshing.'}
                <button 
                  onClick={handleRefreshSubscription} 
                  className="ml-2 inline-flex items-center gap-1 text-red-800 dark:text-red-200 underline hover:text-red-900 dark:hover:text-red-100 font-medium transition-colors" 
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <>
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Refreshing...
                    </>
                  ) : (
                    'Refresh'
                  )}
                </button>
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}      {displayMode === 'planSelection' && (
        <SubscriptionPlansDisplay 
          loading={loading || refreshing}
          availedTrials={availedTrials}
          onSelectPlan={onSelectPlan}
          isOnboarding={completingOnboardingFlag}
          currentSubscription={subscription}
        />
      )}      {displayMode === 'active' && (
        <ActiveSubscriptionDisplay
          subscription={subscription}
          loading={loading}
          refreshing={refreshing}
          onRefresh={handleRefreshSubscription}
          onManageSubscription={handleManageSubscription}
          onUpgradeToPro={handleUpgradeToPro}
          onSelectPlan={onSelectPlan}
          onReactivateSubscription={handleReactivateSubscription}
          currentSubscription={subscription}
          stripeCustomerId={subscription?.stripeCustomerId || 
                            currentUserFromContext?.stripeCustomerId || 
                            user?.stripeCustomerId}
        />
      )}      {displayMode === 'trialEnded' && (
        <TrialEndedNotice
          loading={loading}
          refreshing={refreshing}
          onRefresh={handleRefreshSubscription}
          onManageSubscription={handleManageSubscription}
          onSelectPlan={onSelectPlan}
          currentSubscription={subscription}
          stripeCustomerId={subscription?.stripeCustomerId || 
                          currentUserFromContext?.stripeCustomerId || 
                          user?.stripeCustomerId}
          availedTrials={availedTrials}
          showManageButton={false}
          hideTrialOptions={true}
        />
      )}      {displayMode === 'expired' && (
        <ExpiredSubscriptionDisplay
          subscription={subscription}
          loading={loading}
          refreshing={refreshing}
          onRefresh={handleRefreshSubscription}
          onManageSubscription={handleManageSubscription}
          onSelectPlan={onSelectPlan}
          currentSubscription={subscription}
          stripeCustomerId={subscription?.stripeCustomerId || 
                          currentUserFromContext?.stripeCustomerId || 
                          user?.stripeCustomerId}
        />
      )}
    </div>
  );
};

export default BillingSettings;